import type { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useGlobalManualLocationOrder } from "@/contexts/Staffing/GlobalManualLocationOrderContext";
import { useUserSettingsContext } from "@/contexts/Staffing/UserSettingsContext";
import { useWeekViewContext } from "@/contexts/WeekView";
import { useDepartmentFlags } from "@/hooks/useDepartmentFlags";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { censusDisplayGroups, displayGroupKey } from "@/utils/censusDisplayGroups";
import { departmentLocationOfShift, jobGroupCountsOf, roleGroupCountsOf } from "@/utils/censusGroupCounts";
import {
	ALLOW_AVAILABILITY_FALLBACK,
	byDimensionsOf,
	type LadderFilter,
	resolveLocationCensus,
	scoreAdjustedRequiredForLocation,
} from "@/utils/censusResolution";
import { demoRequiredForDay } from "@/utils/demoRequiredStaff";
import type { DepartmentLocation, GroupedNurses, NurseFullRole, NurseInShift } from "@/utils/models";
import { LocationMode, NurseFullRoleShort, NurseRole } from "@/utils/models";
import { getRoleGroupsForRole } from "@/utils/roles";
import { filterNursesForCoverage, filterNursesForStats } from "@/utils/statsCalculations";
import type { TrainingPpdCounting } from "@/utils/trainingPpdCounting";
import { buildNurseUnionMap, floorToIndex, getCensus, getShiftWorkHours } from "@/utils/utilFunctions";
import type { CalculatedDayViewGaps } from "../DayViewGaps/utils";

/**
 * The bucket for anything the current grouping mode cannot place.
 *
 * DEFAULT: every `displayGroupKey(...) ?? UNASSIGNED_GROUP_KEY` in this file falls back here when a
 * location has no value for the dimensions the mode groups by — no floor in Floor mode, no unit in
 * Unit mode, either missing in Both mode, a blank name in Auto. That is deliberate and it is a
 * DISPLAY decision only: the alternative is dropping those rows, which would hide real staff from
 * the table. It is safe precisely because nothing here feeds staffing math — `censusDisplayGroups`
 * prices headers, and per-location census and required always come from `resolveLocationCensus` /
 * `requiredAmountForLocation`, neither of which sees this key.
 *
 * The consequence to know: "has no unit/floor" and "is genuinely unassigned" render as one bucket.
 * Distinguishing them is a product call, not a code one — see the note in `censusDisplayGroups`.
 */
const UNASSIGNED_GROUP_KEY = "Unassigned";

/**
 * The census shown on the Unassigned bucket: the DEPARTMENT total, minus what the visible groups
 * claimed. Those are the patients no rendered bucket accounted for, which is what the staff sitting
 * in Unassigned are looking after.
 *
 * Measured against `totalCensus` and NOT against the sum of the display-group headers. Summing the
 * headers looks equivalent but is not: `assignedCensus` adds up the very same per-group numbers, so
 * header-sum minus assigned subtracts a set from itself and yields 0 — and `calculateHPPD` returns 0
 * for a zero census, so the bucket showed no HPPD however many nurses were in it.
 *
 * Shared by both grouping flows on purpose. They were computing it separately from identical
 * ingredients, so a change to the rule could land in one and not the other and leave the same day
 * showing a different Unassigned HPPD depending on the grouping mode.
 */
const unassignedResidualCensus = (totalCensus: number, assignedCensus: number): number => Math.max(totalCensus - assignedCensus, 0);

export type LocationFilters = {
	roles?: NurseFullRole[] | "All";
	timeRange?: { start: DateTime; end: DateTime };
};

/** Check whether a DepartmentLocation matches the active role/time filters. */
function doesLocationMatchFilters(loc: DepartmentLocation, filters?: LocationFilters): boolean {
	if (!filters) return true;

	// Role filter: skip locations whose roles don't overlap with filter roles
	if (filters.roles && filters.roles !== "All") {
		const locHasAllRole = loc.roles.includes("all");
		if (!locHasAllRole) {
			const hasOverlap = filters.roles.some((r) => loc.roles.includes(r));
			if (!hasOverlap) return false;
		}
	}

	// Time filter: skip locations whose shift window doesn't overlap the filter range
	if (filters.timeRange && loc.shift_start && loc.shift_end) {
		const baseDate = filters.timeRange.start.startOf("day");
		const [startH, startM] = loc.shift_start.split(":").map(Number);
		const [endH, endM] = loc.shift_end.split(":").map(Number);
		const locStart = baseDate.set({ hour: startH, minute: startM });
		let locEnd = baseDate.set({ hour: endH, minute: endM });
		// Handle overnight shifts (e.g. 20:00 → 07:00)
		if (locEnd <= locStart) locEnd = locEnd.plus({ days: 1 });

		if (locEnd <= filters.timeRange.start || locStart >= filters.timeRange.end) {
			return false;
		}
	}

	return true;
}

export const useCensusGrouping = (date: DateTime) => {
	const { chosenDepartment } = useChosenDepartment();
	const { isHospitalView } = useDepartmentFlags();
	const isRealHospitalDemo = isHospitalView;
	// The weeks' own people, so the group HPPD is right before the roster loads.
	const { schedulePeople, weekOfDate } = useWeekViewContext();
	const { settings } = useUserSettingsContext();
	const { customStringOrderMap } = useGlobalManualLocationOrder();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const allNurses = jobManagementEnabled ? undefined : schedulePeople;
	// Built from `schedulePeople` directly, NOT `allNurses` — that one is nulled out when the
	// job-management flag is on, which would silently drop the union gate for every flagged dept.
	const unionByNurseId = useMemo(() => buildNurseUnionMap(schedulePeople), [schedulePeople]);

	const findAllMatchingDepartmentLocations = useCallback(
		(locationName: string, roles: (NurseFullRole | "all")[]): DepartmentLocation[] => {
			return chosenDepartment.locations.filter((loc) => {
				if (loc.location !== locationName) return false;
				if (roles.includes("all")) return true;
				return roles.some((role) => role !== "all" && loc.roles.includes(role));
			});
		},
		[chosenDepartment.locations],
	);

	const { countTraineeTowardsPpd, countOrientationTowardsPpd, countEducationTowardsPpd } = chosenDepartment;
	// Rebuilt only when a switch changes, so `calculateHPPD` doesn't change with every new department object.
	const trainingPpdCounting = useMemo<TrainingPpdCounting>(
		() => ({ countTraineeTowardsPpd, countOrientationTowardsPpd, countEducationTowardsPpd }),
		[countTraineeTowardsPpd, countOrientationTowardsPpd, countEducationTowardsPpd],
	);

	const calculateHPPD = useCallback(
		(shifts: NurseInShift[], census: number): number => {
			if (!census) return 0;

			const totalHours = shifts
				.filter((shift) => filterNursesForStats(shift, jobManagementEnabled, allNurses, trainingPpdCounting, false))
				.reduce(
					(sum, nurse) =>
						sum +
						getShiftWorkHours(
							nurse.departmentId,
							nurse.endTime,
							nurse.startTime,
							chosenDepartment.breakRulesConfig || [],
							NurseRole[NurseFullRoleShort[nurse.role] as keyof typeof NurseRole],
							nurse.type,
							nurse.jobId ? [nurse.jobId] : [],
							jobManagementEnabled,
							unionByNurseId.get(nurse.nurseId) ?? "",
						),
					0,
				);

			return totalHours / census;
		},
		[chosenDepartment.breakRulesConfig, jobManagementEnabled, allNurses, trainingPpdCounting, unionByNurseId],
	);

	const dayAbbr = useMemo(() => date.setLocale("en").toFormat("ccc").toLowerCase(), [date]);

	// The day's canonical dimension map plus the department total — everything the census resolver
	// needs. Deliberately independent of settings.censusByLocation: that setting groups ROWS, and
	// letting it feed the arithmetic is what allowed two users of one department to see different
	// required amounts for the same shift.
	const { byDimensions, totalCensus, demoRequired } = useMemo(() => {
		const dayWeek = weekOfDate(date);
		const week = dayWeek?.week;
		// -1 is "not in the weeks", as the position lookups say it; the Day view shows only once the day is in them.
		const dayIndex = dayWeek === undefined ? -1 : dayWeek.dayIndex;
		return {
			byDimensions: byDimensionsOf(week?.weeklyShifts[0]?.days[dayIndex]?.census_grouping),
			totalCensus: week === undefined ? 0 : getCensus(week, dayIndex),
			// Demo departments only (patient score or node split); `null` elsewhere.
			demoRequired: demoRequiredForDay({ department: chosenDepartment, week, dayIndex, isRealHospitalDemo }),
		};
	}, [date, weekOfDate, isRealHospitalDemo, chosenDepartment]);

	/**
	 * Census per DISPLAY GROUP — the number shown on each section header.
	 *
	 * This is the one place `censusByLocation` still legitimately matters: it decides how rows are
	 * grouped, so it decides which buckets get a header number. It no longer decides how any
	 * individual location's census or required amount is computed — that is the canonical resolver's
	 * job, and keeping the two separate is the point of this refactor.
	 *
	 * Auto mode rolls each location's resolved census up by location NAME so that same-named variants
	 * (a Nurse row and an Aide row of "Assignment 1") share one header, matching how the other modes
	 * aggregate. `undefined` still means "no census data", which callers rely on.
	 */
	const censusGrouping = useMemo<Record<string, number> | undefined>(() => {
		// `undefined` means "no census data at all", which callers distinguish from an empty grouping.
		// Census EXISTS whenever `totalCensus` is positive, even with no dimensions reported: an
		// all-unmapped department emits an empty `byDimensions` by construction, so testing that map
		// alone reported "no census" for a day that has one and zeroed every location.
		if (Object.keys(byDimensions).length === 0 && totalCensus <= 0) return undefined;
		const groups = censusDisplayGroups(settings.censusByLocation, byDimensions, totalCensus, chosenDepartment.locations, chosenDepartment.statsMode);
		return Object.fromEntries(groups.map((group) => [group.key, group.census]));
	}, [byDimensions, totalCensus, settings.censusByLocation, chosenDepartment.locations, chosenDepartment.statsMode]);

	/**
	 * Sum the ratio-ladder requirements for a set of locations. Census resolves PER LOCATION through
	 * the canonical resolver, so badges, gaps, the printout and the backend all agree by construction.
	 * Callers that need a fixed census for the whole group pass `censusOverride`.
	 */
	const calculateRatios = useCallback(
		(
			locationsInGroup: DepartmentLocation[],
			censusOverride?: number,
		): { totalRatio: number; roleGroupRatios: Record<string, number>; jobGroupRatios: Record<string, number> } => {
			let totalRatio = 0;
			const roleGroupRatios: Record<string, number> = {};
			const jobGroupRatios: Record<string, number> = {};
			const roleGroups = chosenDepartment.role_groups ?? {};

			for (const location of locationsInGroup) {
				// A node-split location ignores `censusOverride`: its number is the shift's node, not a census.
				const census = censusOverride ?? resolveLocationCensus(location, byDimensions, totalCensus, chosenDepartment.statsMode, ALLOW_AVAILABILITY_FALLBACK);
				// Same helper as every other view, so the demo adjustment (each shift's own score, or the
				// node split) can never drift from them.
				const requiredFor = (ladderFilter: LadderFilter): number =>
					scoreAdjustedRequiredForLocation(location, census, chosenDepartment.statsMode, dayAbbr, ladderFilter, demoRequired);

				// Role-group attribution.
				const servedRoleGroups = new Set<string>();
				for (const role of location.roles) {
					if (role === "all") {
						for (const groupName of Object.keys(roleGroups)) servedRoleGroups.add(groupName);
					} else {
						for (const groupName of getRoleGroupsForRole(role, chosenDepartment)) servedRoleGroups.add(groupName);
					}
				}
				for (const groupName of servedRoleGroups) {
					const amount = requiredFor({ kind: "roles", roles: (roleGroups[groupName] ?? []) as NurseRole[] });
					if (amount <= 0) continue;
					totalRatio += amount;
					roleGroupRatios[groupName] = (roleGroupRatios[groupName] || 0) + amount;
				}

				// Job attribution
				if (location.jobId) {
					const amount = requiredFor({ kind: "job", jobId: location.jobId });
					if (amount > 0) jobGroupRatios[location.jobId] = (jobGroupRatios[location.jobId] || 0) + amount;
				}
			}

			return { totalRatio, roleGroupRatios, jobGroupRatios };
		},
		[chosenDepartment, dayAbbr, byDimensions, totalCensus, demoRequired],
	);

	const getAggregateGroupedNurses = useCallback(
		(nurses: NurseInShift[]): GroupedNurses => {
			const filteredNurses = nurses.filter((n) => filterNursesForCoverage(n, false));
			const roleGroupCounts = roleGroupCountsOf(filteredNurses, chosenDepartment);
			const jobGroupCounts = jobGroupCountsOf(filteredNurses);

			// The aggregate "All" row picks whichever grouping produces the higher total requirement.
			// Both sides now resolve through the canonical resolver via censusDisplayGroups, so this row
			// agrees with the per-group rows instead of reading the deprecated maps directly.
			const summarise = (mode: LocationMode) => {
				let total = 0;
				const roleRatios: Record<string, number> = {};
				const jobRatios: Record<string, number> = {};
				let census = 0;
				for (const group of censusDisplayGroups(mode, byDimensions, totalCensus, chosenDepartment.locations, chosenDepartment.statsMode)) {
					const locs = chosenDepartment.locations.filter((loc) => displayGroupKey(loc, mode) === group.key);
					const result = calculateRatios(locs, group.census);
					total += result.totalRatio;
					census += group.census;
					for (const [rg, r] of Object.entries(result.roleGroupRatios)) roleRatios[rg] = (roleRatios[rg] || 0) + r;
					for (const [jg, r] of Object.entries(result.jobGroupRatios)) jobRatios[jg] = (jobRatios[jg] || 0) + r;
				}
				return { total, roleRatios, jobRatios, census };
			};

			const byFloor = summarise(LocationMode.Floor);
			const byUnit = summarise(LocationMode.Unit);
			const chosen = byFloor.total >= byUnit.total ? byFloor : byUnit;
			const aggregateCensus = chosen.census;

			return {
				locationKey: "all",
				locationName: "All",
				censusCount: aggregateCensus,
				nurses,
				roleGroupCounts,
				roleGroupRatios: chosen.roleRatios,
				jobGroupCounts,
				jobGroupRatios: chosen.jobRatios,
				totalRatio: chosen.total,
				hppd: calculateHPPD(nurses, aggregateCensus),
			};
		},
		[chosenDepartment, calculateRatios, calculateHPPD, byDimensions, totalCensus],
	);

	const groupNursesByLocation = useCallback(
		(nurses: NurseInShift[], filters?: LocationFilters): GroupedNurses[] => {
			if (settings.censusByLocation === LocationMode.None) {
				const filteredNursesForCounts = nurses.filter((n) => filterNursesForCoverage(n, false));
				const roleGroupCounts: Record<string, number> = roleGroupCountsOf(filteredNursesForCounts, chosenDepartment);
				const jobGroupCounts: Record<string, number> = jobGroupCountsOf(filteredNursesForCounts);

				// No location grouping: sum required over ALL matching config locations (not just
				// the staffed ones), so unstaffed locations still contribute their required —
				// matching the weekly view and the other census modes. Each location still resolves
				// its own census through the canonical resolver, so the total here equals the sum of
				// the per-group rows every other mode shows.
				//
				// The single "All" bucket IS the department, so its census is the department day
				// total. Deriving it from `censusGrouping` instead would yield 0, because None mode
				// has no display groups to sum — a display preference silently zeroing a number and
				// the HPPD computed from it, which is exactly what this refactor removes.
				const totalCensusForAll = totalCensus;
				const filteredLocations = chosenDepartment.locations.filter((loc) => doesLocationMatchFilters(loc, filters));
				const { totalRatio, roleGroupRatios, jobGroupRatios } = calculateRatios(filteredLocations);

				return [
					{
						locationKey: "all",
						locationName: "All",
						censusCount: totalCensusForAll,
						nurses,
						roleGroupCounts,
						roleGroupRatios,
						jobGroupCounts,
						jobGroupRatios,
						totalRatio,
						hppd: calculateHPPD(nurses, totalCensusForAll),
					},
				];
			}

			// Group nurses by floor, unit, both, or department location (Auto)
			const isBothMode = settings.censusByLocation === LocationMode.Both;
			const groups = new Map<string, NurseInShift[]>();
			const groupToLocations = new Map<string, DepartmentLocation[]>();

			for (const nurseShift of nurses) {
				if (!nurseShift.location) {
					const existing = groups.get(UNASSIGNED_GROUP_KEY) || [];
					groups.set(UNASSIGNED_GROUP_KEY, [...existing, nurseShift]);
					continue;
				}

				const departmentLocation = departmentLocationOfShift(nurseShift, chosenDepartment.locations, jobManagementEnabled);
				if (!departmentLocation) {
					const existing = groups.get(UNASSIGNED_GROUP_KEY) || [];
					groups.set(UNASSIGNED_GROUP_KEY, [...existing, nurseShift]);
					continue;
				}

				// Track department locations for this group. `displayGroupKey` is the same helper that
				// builds the census header keys, so a row can no longer land in a bucket the header
				// arithmetic does not know about. Auto mode groups by name, so same-named location
				// variants (e.g. Nurse + Aide versions of "Assignment 1") roll up together.
				// DEFAULT: see UNASSIGNED_GROUP_KEY — a shift whose location has no value for this mode's
				// dimensions is shown under Unassigned rather than dropped from the table.
				const locationKey: string = displayGroupKey(departmentLocation, settings.censusByLocation) ?? UNASSIGNED_GROUP_KEY;

				const existing = groups.get(locationKey) || [];
				groups.set(locationKey, [...existing, nurseShift]);
			}

			const filteredLocationsForGrouping = chosenDepartment.locations.filter((loc) => doesLocationMatchFilters(loc, filters));
			for (const loc of filteredLocationsForGrouping) {
				// DEFAULT: see UNASSIGNED_GROUP_KEY — an unmappable location still needs a bucket so its
				// ratio row renders; it just cannot claim a dimension it has no value for.
				const locKey: string = displayGroupKey(loc, settings.censusByLocation) ?? UNASSIGNED_GROUP_KEY;

				const existingLocations = groupToLocations.get(locKey) || [];
				if (!existingLocations.find((existing) => existing.id === loc.id)) {
					groupToLocations.set(locKey, [...existingLocations, loc]);
				}
			}

			if (censusGrouping) {
				for (const [locationKey, censusCo] of Object.entries(censusGrouping)) {
					if (censusCo > 0 && !groups.has(locationKey) && locationKey !== UNASSIGNED_GROUP_KEY) {
						groups.set(locationKey, []);
					}
				}
			}

			// Convert to array with census counts and stats
			const result: GroupedNurses[] = [];

			// Only the assigned side is accumulated here; the residual itself comes from
			// `unassignedResidualCensus` so both grouping flows apply one rule.
			let assignedCensus = 0;

			for (const [locationKey, nursesInGroup] of groups.entries()) {
				const censusCo = censusGrouping?.[locationKey] || 0;

				if (locationKey !== UNASSIGNED_GROUP_KEY && censusCo === 0 && nursesInGroup.length === 0) continue;

				// Add to assigned census only once per group
				if (locationKey !== UNASSIGNED_GROUP_KEY) {
					assignedCensus += censusCo;
				}

				const filteredNursesInGroup = nursesInGroup.filter((n) => filterNursesForCoverage(n, false));
				const roleGroupCounts: Record<string, number> = roleGroupCountsOf(filteredNursesInGroup, chosenDepartment);
				const jobGroupCounts: Record<string, number> = jobGroupCountsOf(filteredNursesInGroup);

				const locationsInGroup = groupToLocations.get(locationKey) || [];
				// Census is resolved per-location inside calculateRatios (handles cross-dimension too).
				const { totalRatio, roleGroupRatios, jobGroupRatios } = calculateRatios(locationsInGroup);

				const hppd = calculateHPPD(nursesInGroup, censusCo);

				// In Both mode, format display name from the "unit|floor" key.
				// In Auto mode, the key is already the location name (we aggregate same-named variants).
				let locationName = locationKey;
				if (isBothMode && locationKey.includes("|")) {
					const [unit, floor] = locationKey.split("|");
					locationName = `Unit: ${unit}, Floor: ${floor}`;
				}

				result.push({
					locationKey,
					locationName,
					censusCount: censusCo,
					nurses: nursesInGroup,
					roleGroupCounts,
					roleGroupRatios,
					jobGroupCounts,
					jobGroupRatios,
					totalRatio,
					hppd,
				});
			}

			// Update the unassigned group's displayed census + HPPD. Its ratios already came from the
			// normal per-location loop above, where calculateRatios resolves cross-dimension census.
			const unassignedGroup = result.find((g) => g.locationKey === UNASSIGNED_GROUP_KEY);
			if (unassignedGroup && censusGrouping) {
				unassignedGroup.censusCount = unassignedResidualCensus(totalCensus, assignedCensus);
				unassignedGroup.hppd = calculateHPPD(unassignedGroup.nurses, unassignedGroup.censusCount);
			}

			return result.sort((a, b) => {
				if (a.locationName === UNASSIGNED_GROUP_KEY) return 1;
				if (b.locationName === UNASSIGNED_GROUP_KEY) return -1;

				if (customStringOrderMap) {
					const idxA = customStringOrderMap.get(a.locationName) ?? customStringOrderMap.get(a.locationKey);
					const idxB = customStringOrderMap.get(b.locationName) ?? customStringOrderMap.get(b.locationKey);
					if (idxA !== undefined && idxB !== undefined) return idxA - idxB;
					if (idxA !== undefined) return -1;
					if (idxB !== undefined) return 1;
				}

				if (isBothMode) {
					// Parse "Unit: X, Floor: Y" back to parts for two-level sort
					const parse = (name: string) => {
						const match = name.match(/^Unit: (.+), Floor: (.+)$/);
						return match ? { unit: match[1], floor: match[2] } : null;
					};
					const aParts = parse(a.locationName);
					const bParts = parse(b.locationName);
					if (aParts && bParts) {
						const unitCompare = aParts.unit.localeCompare(bParts.unit);
						if (unitCompare !== 0) return unitCompare;
						const floorA = floorToIndex(aParts.floor);
						const floorB = floorToIndex(bParts.floor);
						if (floorA !== undefined && floorB !== undefined) return floorA - floorB;
						if (floorA !== undefined) return -1;
						if (floorB !== undefined) return 1;
						return aParts.floor.localeCompare(bParts.floor);
					}
				}

				if (settings.censusByLocation === LocationMode.Floor) {
					const floorA = floorToIndex(a.locationKey);
					const floorB = floorToIndex(b.locationKey);
					if (floorA !== undefined && floorB !== undefined) {
						return floorA - floorB;
					}
					if (floorA !== undefined) return -1;
					if (floorB !== undefined) return 1;
				}
				return a.locationName.localeCompare(b.locationName);
			});
		},
		[chosenDepartment, settings.censusByLocation, calculateRatios, calculateHPPD, censusGrouping, jobManagementEnabled, customStringOrderMap, totalCensus],
	);

	const groupLocationsByFloorOrUnit = useCallback(
		(calculatedData: CalculatedDayViewGaps, nurses: NurseInShift[], filters?: LocationFilters) => {
			if (settings.censusByLocation === LocationMode.None) return [];

			const isBothMode = settings.censusByLocation === LocationMode.Both;
			const calculatedDataEntries = Object.entries(calculatedData);
			if (!calculatedDataEntries.length) return [];

			const groups = new Map<string, string[]>();
			const groupToNurses = new Map<string, NurseInShift[]>();
			const groupToLocations = new Map<string, DepartmentLocation[]>();

			for (const [key, rowData] of calculatedDataEntries) {
				const allMatchingLocations = findAllMatchingDepartmentLocations(rowData.locationName, rowData.roles);
				const matchingLocations = allMatchingLocations.filter((loc) => doesLocationMatchFilters(loc, filters));

				// If no matching locations, add to unassigned
				if (!matchingLocations.length) {
					const existing = groups.get(UNASSIGNED_GROUP_KEY) || [];
					groups.set(UNASSIGNED_GROUP_KEY, [...existing, key]);
					continue;
				}

				// For each matching location, determine its bucket via the shared helper — the same one
				// that builds the census header keys, so rows and headers cannot drift apart.
				for (const loc of matchingLocations) {
					// DEFAULT: see UNASSIGNED_GROUP_KEY — same rule as the daily flow, kept identical so the
					// two grouping paths cannot disagree about where an unmappable location lands.
					const groupKey: string = displayGroupKey(loc, settings.censusByLocation) ?? UNASSIGNED_GROUP_KEY;

					const existing = groups.get(groupKey) || [];
					if (!existing.includes(key)) groups.set(groupKey, [...existing, key]);

					const existingLocations = groupToLocations.get(groupKey) || [];
					if (!existingLocations.find((existingLoc) => existingLoc.id === loc.id)) {
						existingLocations.push(loc);
						groupToLocations.set(groupKey, existingLocations);
					}
				}
			}

			// Map nurses to groups based on their location
			for (const nurse of nurses) {
				if (!nurse.location) {
					const existing = groupToNurses.get(UNASSIGNED_GROUP_KEY) || [];
					groupToNurses.set(UNASSIGNED_GROUP_KEY, [...existing, nurse]);
					continue;
				}

				const departmentLocation = departmentLocationOfShift(nurse, chosenDepartment.locations, jobManagementEnabled);
				if (!departmentLocation) {
					const existing = groupToNurses.get(UNASSIGNED_GROUP_KEY) || [];
					groupToNurses.set(UNASSIGNED_GROUP_KEY, [...existing, nurse]);
					continue;
				}

				// DEFAULT: see UNASSIGNED_GROUP_KEY — a nurse whose location has no value for this mode's
				// dimensions stays visible under Unassigned instead of disappearing from the view.
				const groupKey: string = displayGroupKey(departmentLocation, settings.censusByLocation) ?? UNASSIGNED_GROUP_KEY;

				const existing = groupToNurses.get(groupKey) || [];
				groupToNurses.set(groupKey, [...existing, nurse]);
			}

			if (groupToNurses.get(UNASSIGNED_GROUP_KEY)?.length && !groups.has(UNASSIGNED_GROUP_KEY)) {
				// `groups` is built from the calculated location data, so it can lack an Unassigned key
				// even when nurses have no location (or no matching config). Add it so those nurses are
				// still emitted and counted in the Unassigned group.
				groups.set(UNASSIGNED_GROUP_KEY, []);
			}

			// Convert to array with census counts and stats
			const result: Array<{
				groupKey: string;
				groupName: string;
				censusCount: number;
				nursesCount: number;
				nurses: NurseInShift[];
				locationKeys: string[];
				roleGroupCounts: Record<string, number>;
				roleGroupRatios: Record<string, number>;
				jobGroupCounts: Record<string, number>;
				jobGroupRatios: Record<string, number>;
				hppd: number;
			}> = [];

			// Sum of the DISPLAY groups' censuses — see the note in groupNursesByLocation; this is not
			// the department total.
			let assignedCensus = 0;

			// Calculate census counts, role groups, and HPPD for each group
			for (const [groupKey, locationKeys] of groups.entries()) {
				const censusCo = censusGrouping?.[groupKey] || 0;
				const nursesInGroup = groupToNurses.get(groupKey) || [];

				// Add to assigned census only once per group
				if (groupKey !== UNASSIGNED_GROUP_KEY) {
					assignedCensus += censusCo;
				}

				const filteredNursesInGroup = nursesInGroup.filter((n) => filterNursesForCoverage(n, false));
				const roleGroupCounts: Record<string, number> = roleGroupCountsOf(filteredNursesInGroup, chosenDepartment);
				const jobGroupCounts: Record<string, number> = jobGroupCountsOf(filteredNursesInGroup);

				// Required: sum over the group's UNIQUE locations (deduped by id in groupToLocations),
				// not per calculated-data key — a multi-role location appears under several keys (one
				// per role/job row) and summing per-key would count its ratio multiple times. This
				// matches groupNursesByLocation (the standard daily table). Skip Unassigned (no ladder).
				// Census is resolved per-location inside calculateRatios (handles cross-dimension too).
				const { roleGroupRatios, jobGroupRatios } =
					groupKey !== UNASSIGNED_GROUP_KEY
						? calculateRatios(groupToLocations.get(groupKey) || [])
						: { roleGroupRatios: {} as Record<string, number>, jobGroupRatios: {} as Record<string, number> };

				const groupName = isBothMode && groupKey.includes("|") ? `Unit: ${groupKey.split("|")[0]}, Floor: ${groupKey.split("|")[1]}` : groupKey;

				result.push({
					groupKey,
					groupName,
					censusCount: censusCo,
					nursesCount: nursesInGroup.length,
					nurses: nursesInGroup,
					locationKeys,
					roleGroupCounts,
					roleGroupRatios,
					jobGroupCounts,
					jobGroupRatios,
					hppd: calculateHPPD(nursesInGroup, censusCo),
				});
			}

			// Update the unassigned group's census, ratios, and HPPD. Ratios are resolved per-location
			// via calculateRatios, which now applies the cross-dimension census fallback uniformly.
			const unassignedGroup = result.find((g) => g.groupKey === UNASSIGNED_GROUP_KEY);
			if (unassignedGroup && censusGrouping) {
				unassignedGroup.censusCount = unassignedResidualCensus(totalCensus, assignedCensus);

				const unassignedLocations = groupToLocations.get(UNASSIGNED_GROUP_KEY) || [];
				const { roleGroupRatios: unassignedRoleGroupRatios, jobGroupRatios: unassignedJobGroupRatios } = calculateRatios(unassignedLocations);

				unassignedGroup.roleGroupRatios = unassignedRoleGroupRatios;
				unassignedGroup.jobGroupRatios = unassignedJobGroupRatios;
				unassignedGroup.hppd = calculateHPPD(groupToNurses.get(UNASSIGNED_GROUP_KEY) || [], unassignedGroup.censusCount);
			}

			// Sort alphabetically with UNASSIGNED_GROUP_KEY always last
			return result.sort((a, b) => {
				if (a.groupName === UNASSIGNED_GROUP_KEY) return 1;
				if (b.groupName === UNASSIGNED_GROUP_KEY) return -1;

				if (isBothMode) {
					const parse = (name: string) => {
						const match = name.match(/^Unit: (.+), Floor: (.+)$/);
						return match ? { unit: match[1], floor: match[2] } : null;
					};
					const aParts = parse(a.groupName);
					const bParts = parse(b.groupName);
					if (aParts && bParts) {
						const unitCompare = aParts.unit.localeCompare(bParts.unit);
						if (unitCompare !== 0) return unitCompare;
						const floorA = floorToIndex(aParts.floor);
						const floorB = floorToIndex(bParts.floor);
						if (floorA !== undefined && floorB !== undefined) return floorA - floorB;
						if (floorA !== undefined) return -1;
						if (floorB !== undefined) return 1;
						return aParts.floor.localeCompare(bParts.floor);
					}
				}

				if (settings.censusByLocation === LocationMode.Floor) {
					const floorA = floorToIndex(a.groupKey);
					const floorB = floorToIndex(b.groupKey);
					if (floorA !== undefined && floorB !== undefined) {
						return floorA - floorB;
					}
					if (floorA !== undefined) return -1;
					if (floorB !== undefined) return 1;
				}
				return a.groupName.localeCompare(b.groupName);
			});
		},
		[
			chosenDepartment,
			settings.censusByLocation,
			calculateRatios,
			calculateHPPD,
			censusGrouping,
			jobManagementEnabled,
			findAllMatchingDepartmentLocations,
			totalCensus,
		],
	);

	const dailyRequiredRatios = useMemo(
		// Department-wide required for the day: sums the ladder requirement over EVERY config location
		// active this weekday (staffed or not), per role group / job. The daily header uses this for its
		// "required" total so it includes unstaffed locations — matching the gap view and the weekly view
		// (groupNursesByLocation only forms groups for staffed/census-bearing locations, so summing it
		// would drop unstaffed-but-required locations).
		() => calculateRatios(chosenDepartment.locations.filter((loc) => loc.days?.includes(dayAbbr))),
		[calculateRatios, chosenDepartment.locations, dayAbbr, totalCensus],
	);

	return { groupNursesByLocation, groupLocationsByFloorOrUnit, getAggregateGroupedNurses, dailyRequiredRatios };
};
